import { colors, intl, ui, usePrevious } from '@beelday/common'
import { useAuthenticatedUser } from '@beelday/common/src/security'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'app-redux'
import { Config } from 'common/config'
import useMicApi from 'features/microphone/microphone-api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import TwoColumnsVideoGrid from 'room/training-room/ice-breaker/presentation/two-columns-video-grid'
import { SceneVideoUser } from 'video-conference-media'
import useYouTubeStreamingApi from './youtube-streaming-api'
import {
	YouTubeStreamOffsetRequested,
	YouTubeStreamUserReadiness,
	iRequestOffset,
	selectYouTubeStreamingOffsetRequested,
	selectYouTubeStreamingUserReadiness,
	selectYouTubeStreamState,
} from './youtube-streaming-redux'
import { YouTubeStreamState } from './model'
import {
	extractYouTubeVideoId,
	loadYouTubeIframeApi,
	YouTubeIframeApi,
	YouTubePlayer,
} from './youtube-utils'

type Props = {
	streams: Array<SceneVideoUser>
}

export const YouTubeStreamingUser = ({ streams }: Props): JSX.Element => {
	const youtubeStream = useSelector(selectYouTubeStreamState)
	const youtubeStreamOffsetRequested = useSelector(
		selectYouTubeStreamingOffsetRequested
	)
	const youtubeStreamUserReadiness = useSelector(
		selectYouTubeStreamingUserReadiness
	)
	return youtubeStream && youtubeStreamOffsetRequested && streams ? (
		<ConfiguredYouTubeStreamingUser
			youtubeStream={youtubeStream}
			youtubeStreamOffsetRequested={youtubeStreamOffsetRequested}
			audioUserReadiness={youtubeStreamUserReadiness}
			streams={streams}
		/>
	) : (
		<></>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	justify-content: flex-start;
	min-height: 0;
	width: 100%;
	padding: 8px 24px 8px 24px;
	row-gap: 12px;
`

const Title = styled.div`
	font-size: 28px;
	line-height: 36px;
	font-weight: 700;
	align-self: flex-start;
	text-align: left;
	word-break: break-all;

	${ui.responsive.desktop1440} {
		font-size: 24px;
		line-height: 30px;
	}
`

const ControlsContainer = styled(ui.Flex)`
	column-gap: 20px;
	padding: 8px 10px;
	background-color: ${colors.lightPurple};
	width: fit-content;
	border-radius: 50px;
	justify-content: space-between;
	align-items: center;
	max-width: 100%;
	align-self: center;
`

const ControlButton = styled.button`
	height: 40px;
	border-radius: 20px;
	border: 2px solid ${colors.borderLightGray};
	background-color: ${colors.indigoBlue};
	color: ${colors.white};
	padding: 0 14px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		opacity: 0.92;
	}
`

const PlayerColumn = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	row-gap: 16px;
`

const PlayerStage = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
	min-height: 0;
	width: 100%;
`

const PlayerShell = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	aspect-ratio: 16 / 9;
	max-height: 100%;
	margin: 0;
`

const PlayerSurface = styled.div`
	width: 100%;
	height: 100%;

	> div,
	iframe {
		width: 100%;
		height: 100%;
	}
`

const PlayerInteractionBlocker = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	background: transparent;
`

type ConfiguredProps = {
	youtubeStream: YouTubeStreamState
	youtubeStreamOffsetRequested: YouTubeStreamOffsetRequested
	audioUserReadiness: YouTubeStreamUserReadiness
	streams: Array<SceneVideoUser>
}

export const ConfiguredYouTubeStreamingUser = ({
	youtubeStream,
	youtubeStreamOffsetRequested,
	audioUserReadiness,
	streams,
}: ConfiguredProps): JSX.Element => {
	const dispatch = useDispatch()
	const youtubeStreamingApi = useYouTubeStreamingApi()
	const address = useAssertedJoinedRoomAddress()
	const microphoneApi = useMicApi(Config.beeldayBackendUrl)
	const assertedUser = useAuthenticatedUser()

	const playerRef = useRef<YouTubePlayer | null>(null)
	const playerContainerRef = useRef<HTMLDivElement | null>(null)
	const playerShellRef = useRef<HTMLDivElement | null>(null)
	const appliedInitialPlayingOffsetRef = useRef(false)
	const youtubeStreamOffsetRef = useRef(youtubeStream.offset)

	const [currentTime, setCurrentTime] = useState(0)
	const [duration, setDuration] = useState(0)
	const [playerReady, setPlayerReady] = useState(false)
	const [isFullscreen, setIsFullscreen] = useState(false)

	const videoId = useMemo(
		() => extractYouTubeVideoId(youtubeStream.link),
		[youtubeStream.link]
	)

	const { previous: previousState, current: currentState } = usePrevious(
		youtubeStream?.state
	)

	useEffect(() => {
		youtubeStreamOffsetRef.current = youtubeStream.offset
	}, [youtubeStream.offset])

	const playVideo = useCallback(
		(startOffsetMs?: number) => {
			microphoneApi.muteMic(address, assertedUser.id).then(() => {
				if (audioUserReadiness.ready) {
					if (typeof startOffsetMs === 'number') {
						playerRef.current?.seekTo(startOffsetMs / 1000, true)
						setCurrentTime(startOffsetMs / 1000)
					}
					playerRef.current?.playVideo()
				}
			})
		},
		[address, assertedUser.id, audioUserReadiness.ready, microphoneApi]
	)

	const toggleFullscreen = useCallback(async () => {
		const target = playerShellRef.current as
			| (HTMLDivElement & {
					webkitRequestFullscreen?: () => Promise<void> | void
			  })
			| null
		const fullscreenDocument = document as Document & {
			webkitFullscreenElement?: Element | null
			exitFullscreen?: () => Promise<void>
			webkitExitFullscreen?: () => Promise<void> | void
		}

		if (!target) return

		if (
			document.fullscreenElement === target ||
			fullscreenDocument.webkitFullscreenElement === target
		) {
			if (document.exitFullscreen) {
				await document.exitFullscreen()
			} else if (fullscreenDocument.webkitExitFullscreen) {
				await fullscreenDocument.webkitExitFullscreen()
			}
			return
		}

		if (target.requestFullscreen) {
			await target.requestFullscreen()
		} else if (target.webkitRequestFullscreen) {
			await target.webkitRequestFullscreen()
		}
	}, [])

	useEffect(() => {
		const fullscreenDocument = document as Document & {
			webkitFullscreenElement?: Element | null
		}
		const handleFullscreenChange = () => {
			setIsFullscreen(
				document.fullscreenElement === playerShellRef.current ||
					fullscreenDocument.webkitFullscreenElement === playerShellRef.current
			)
		}

		document.addEventListener('fullscreenchange', handleFullscreenChange)
		document.addEventListener(
			'webkitfullscreenchange',
			handleFullscreenChange as EventListener
		)

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange)
			document.removeEventListener(
				'webkitfullscreenchange',
				handleFullscreenChange as EventListener
			)
		}
	}, [])

	useEffect(() => {
		setCurrentTime(0)
		setDuration(0)

		let isActive = true
		setPlayerReady(false)

		if (!videoId || !playerContainerRef.current) return

		loadYouTubeIframeApi().then(api => {
			if (!isActive || !playerContainerRef.current) return
			const yt = api as YouTubeIframeApi
			playerRef.current = new yt.Player(playerContainerRef.current, {
				videoId,
				playerVars: {
					controls: 0,
					cc_load_policy: 0,
					disablekb: 1,
					fs: 1,
					iv_load_policy: 3,
					origin: window.location.origin,
					rel: 0,
					modestbranding: 1,
					playsinline: 1,
				},
				events: {
					onReady: () => {
						setPlayerReady(true)
						setDuration(playerRef.current?.getDuration?.() || 0)
						const iframe = playerRef.current?.getIframe?.()
						if (iframe) {
							iframe.setAttribute(
								'allow',
								'autoplay; encrypted-media; fullscreen'
							)
							iframe.setAttribute('allowfullscreen', 'true')
						}
						playerRef.current?.cueVideoById(
							videoId,
							(youtubeStreamOffsetRef.current || 0) / 1000
						)
					},
					onStateChange: () => undefined,
				},
			})
		})

		return () => {
			isActive = false
			if (playerRef.current?.destroy) {
				playerRef.current.destroy()
			}
			playerRef.current = null
		}
	}, [videoId])

	useEffect(() => {
		if (!playerReady || !playerRef.current) return
		const interval = setInterval(() => {
			const nextTime = playerRef.current?.getCurrentTime?.() || 0
			const nextDuration = playerRef.current?.getDuration?.() || 0
			setCurrentTime(nextTime)
			setDuration(nextDuration)
		}, 500)

		return () => clearInterval(interval)
	}, [playerReady])

	useEffect(() => {
		if (!playerReady || !playerRef.current) return
		playerRef.current.seekTo((youtubeStream?.offset || 0) / 1000, true)
	}, [playerReady, youtubeStream?.offset])

	useEffect(() => {
		if (!playerReady || !playerRef.current) return
		if (!previousState && currentState === 'Playing') {
			const initialStartOffsetMs = appliedInitialPlayingOffsetRef.current
				? undefined
				: youtubeStreamOffsetRef.current + 1000

			if (
				!youtubeStreamOffsetRequested.request.meRequesting &&
				!audioUserReadiness.ready
			) {
				dispatch(iRequestOffset())
				microphoneApi.muteMic(address, assertedUser.id).then(() => {
					youtubeStreamingApi.requestOffset(address)
				})
			}

			if (audioUserReadiness.ready) {
				appliedInitialPlayingOffsetRef.current = true
				playVideo(initialStartOffsetMs)
			}
		}
		if (previousState === 'Stopped' && currentState === 'Playing') {
			playVideo()
		} else if (previousState === 'Paused' && currentState === 'Playing') {
			playVideo()
		} else if (currentState === 'Stopped') {
			playerRef.current?.pauseVideo()
		} else if (currentState === 'Paused') {
			playerRef.current?.pauseVideo()
		}
	}, [
		address,
		assertedUser.id,
		audioUserReadiness.ready,
		currentState,
		dispatch,
		microphoneApi,
		playerReady,
		playVideo,
		previousState,
		youtubeStreamOffsetRequested.request.meRequesting,
		youtubeStreamingApi,
	])

	useEffect(() => {
		if (
			youtubeStreamOffsetRequested.request.requested &&
			!youtubeStreamOffsetRequested.request.meRequesting &&
			!youtubeStreamOffsetRequested.answer.answered
		) {
			youtubeStreamingApi.provideOffset(
				address,
				Math.floor((playerRef.current?.getCurrentTime?.() || 0) * 1000)
			)
		}
	}, [address, youtubeStreamOffsetRequested, youtubeStreamingApi])

	return (
		<TwoColumnsVideoGrid sceneVideoUsers={streams}>
			<Container>
				<Title>{youtubeStream.name}</Title>
				<PlayerColumn>
					<PlayerStage>
						<PlayerShell ref={playerShellRef}>
							<PlayerSurface ref={playerContainerRef} />
							<PlayerInteractionBlocker />
						</PlayerShell>
					</PlayerStage>
					<ControlsContainer>
						<ui.H5 style={{ color: colors.white }}>
							{formatTime(currentTime)} / {formatTime(duration)}
						</ui.H5>
						<ControlButton onClick={toggleFullscreen} type="button">
							<intl.Translate>
								{isFullscreen
									? 'youtube_stream.controls.exit_fullscreen'
									: 'youtube_stream.controls.fullscreen'}
							</intl.Translate>
						</ControlButton>
					</ControlsContainer>
				</PlayerColumn>

				<ui.H5 style={{ maxWidth: '100%', wordBreak: 'break-all' }}>
					{youtubeStream.description}
				</ui.H5>
			</Container>
		</TwoColumnsVideoGrid>
	)
}

const formatTime = (time: number): string => {
	const minutes = Math.floor(time / 60)
	const seconds = Math.floor(time % 60)
		.toString()
		.padStart(2, '0')
	return `${minutes}:${seconds}`
}
